import { Box, Chip } from "@mui/material";
import HomeTableSkeleton from "src/components/common/HomeTableSkeleton";
import { AxiosResponse } from "axios";
import { useState, useEffect } from "react";
import { useQuery } from "react-query";
import { useDispatch } from "react-redux";
import {
  useNavigate,
  createSearchParams,
  Link as RouterLink,
  useSearchParams,
} from "react-router-dom";
import { DashboardWrapper } from "src/components/common/DashboardWrapper";
import { Button } from "src/components/common/FormComponents";
import HorizontalTabs from "src/components/common/HorizontalTabs";
import MultiSelect from "src/components/common/MultiSelect";
import SearchBar from "src/components/common/SearchField";
import { usePersistedFilters } from "src/hooks/usePersistedFilters";
import { useLandOwnershipEntityOptions } from "src/hooks/useLandOwnershipEntityOptions";

import {
  memorandumTab,
  ExportStatusMessages2,
  statusMessages2,
} from "src/constants/common";
import {
  getAllProjectsData,
  refetchAllProjects,
} from "src/redux/slices/dashboard/projects";
import EmptyView from "src/components/common/EmptyView";
import { getAllRegistration } from "src/utils/api/dashboard";
import { getStates } from "src/utils/api/roles";
import { dateAndTimeStamp, downloadSheet, formatINR } from "src/utils/helper";
import { path } from "src/constants/path";
import DataGrid from "src/components/common/DataGrid";
import { getStyles } from "src/styles/theme";
import defaultStyles, { StylesClasses } from "./styles";
import { RenderMOUButton } from "src/components/feature/DetailedProjectReport/RenderComponent";

const MOU_STATUS_LABELS: Record<string, string> = {
  DPR_APPROVED: "DPR Approved",
  MOU_DRAFT: "Draft",
  MOU_SUBMIT: "Submitted",
  MOU_DNO_QUERY: "Query from DNO",
  MOU_UNDER_SNO: "Under SNO Review",
  MOU_SNO_QUERY: "Query from SNO",
  MOU_UNDER_ADMIN: "Under Admin Review",
  MOU_ADMIN_QUERY: "Query from Admin",
  MOU_APPROVED: "Approved",
};

const MOU_STATUS_COLORS: Record<string, string> = {
  DPR_APPROVED: "#36B1A0",
  MOU_DRAFT: "#757575",
  MOU_SUBMIT: "#1976d2",
  MOU_DNO_QUERY: "#ed6c02",
  MOU_UNDER_SNO: "#1976d2",
  MOU_SNO_QUERY: "#ed6c02",
  MOU_UNDER_ADMIN: "#1976d2",
  MOU_ADMIN_QUERY: "#ed6c02",
  MOU_APPROVED: "#2e7d32",
};

const ALL_MOU_TAB =
  "MOU_SUBMIT,MOU_DNO_QUERY,MOU_UNDER_SNO,MOU_SNO_QUERY,MOU_UNDER_ADMIN,MOU_ADMIN_QUERY,MOU_APPROVED";

const isAllTab = (tabValue: string) => tabValue === ALL_MOU_TAB;

const Memorandum = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = searchParams.get("currentTab");
  const { persisted, saveFilters } = usePersistedFilters("mou");
  const [activeTab, setActiveTab] = useState(
    tab ?? persisted?.activeTab ?? ALL_MOU_TAB,
  );
  const [states, setStates] = useState<any>([]);
  const [projectData, setProjectData] = useState<any>({
    count: 0,
    rows: [],
  });
  const [entityActiveTab, setEntityActiveTab] = useState(
    tab ?? persisted?.activeTab ?? ALL_MOU_TAB,
  );

  const styles = getStyles<StylesClasses>(defaultStyles);

  const [queryParams, setQueryParams] = useState<{
    mouStatus: any;
    dprStatus?: string;
    offset: number;
    limit: number;
    sortBy: string;
    searchText: string;
    projectStateIds?: string[];
    download: boolean;
    entityIds?: string[];
    newSelection: any;
  }>({
    mouStatus: activeTab,
    ...(isAllTab(activeTab) ? { dprStatus: "DPR_APPROVED" } : {}),
    offset: persisted?.queryParams?.offset ?? 0,
    limit: 50,
    sortBy: persisted?.queryParams?.sortBy ?? "updated_at:DESC",
    searchText: persisted?.queryParams?.searchText ?? "",
    projectStateIds: persisted?.queryParams?.projectStateIds ?? undefined,
    download: false,
    entityIds: persisted?.queryParams?.entityIds ?? undefined,
    newSelection: 1,
  });

  const [filterKey, setFilterKey] = useState(
    persisted?.filterKey ?? { projectStateIds: null },
  );

  const [entityfilterKey, entitySetFilterKey] = useState(
    persisted?.entityFilterKey ?? { enetityStateIds: null },
  );

  useEffect(() => {
    const { download, ...qp } = queryParams;
    saveFilters({
      queryParams: qp,
      filterKey,
      entityFilterKey: entityfilterKey,
      activeTab: entityActiveTab,
    });
  }, [queryParams, filterKey, entityfilterKey, entityActiveTab]);

  useQuery(["getStatesType"], () => getStates(), {
    onSuccess: (data) => {
      setStates(data.data);
    },
    refetchOnMount: true,
  });

  const { isFetching: isEntityFetching } = useQuery(
    ["mouProjects", queryParams],
    () =>
      getAllRegistration({
        ...queryParams,
        stateIds: queryParams?.projectStateIds
          ? queryParams?.projectStateIds.join(",")
          : undefined,
      }),
    {
      onSuccess: (data) => {
        dispatch(getAllProjectsData(data.data?.rows));
        const rows = data?.data?.rows?.map((row) => ({
          data: row,
          sno: row?.serialNo,
          id: row.id,
          status: row.status,
          mouStatus: row?.mou?.status || "DPR_APPROVED",
          rawDprStatus: "DPR_APPROVED",
          entityName: row?.user?.firstName,
          netPlantationArea: row?.netPlantationArea ?? "-",
          registrationNo: row.registrationNo,
          state: row.state,
          district: row.district,
          division: row?.division,
          allocatedivision:
            row?.projectDetail?.organizationAffiliationDetail?.userDetail
              ?.division,
          range: row?.projectDetail?.landSpecification?.range,
          treeCanopy: row?.treeCannopyDensity ?? "-",
          landArea: row?.projectDetail?.landArea,
          areaUnit: `${row.totalArea}`,
          demandNote: formatINR(row.demandNote) ?? "-",
          estimateAmount: row.demandNote
            ? formatINR(
                +`${Number(row?.demandNote)}` + +`${Number(row?.icfreCharges)}`,
              )
            : "-",
          totalAmountRecived: formatINR(row.amountPaid) ?? "-",
          ecoRestorationCostRecived: formatINR(row.ecoRestorationCost) ?? "-",
          adminChargesRecived: formatINR(row.adminCharges) ?? "-",
          remark: row.remark,
          created_at: dateAndTimeStamp(row?.created_at, "IST"),
          updated_at: dateAndTimeStamp(row.updated_at, "IST"),
        }));

        if (queryParams.download) {
          interface ExportItem {
            "S.NO"?: any;
            EPID?: any;
            "Registration No"?: any;
            "Entity Name"?: any;
            State?: any;
            District?: any;
            Division?: any;
            "Allocate Division"?: any;
            Range?: any;
            "Total Area"?: any;
            "Area Selected"?: any;
            Remark?: any;
            "Created At"?: any;
            "Updated At"?: any;
            Status?: any;
            "Final Area"?: any;
            "Tree Canopy Density"?: any;
            Eco_Restoration_Cost?: any;
            DemandNote_Amount?: any;
            "Total Amount Received"?: any;
            "Eco-Restoration Cost Received"?: any;
            "Admin Charges Received"?: any;
          }

          const exportData: ExportItem[] = rows.map((item) => {
            const status = statusMessages2[item?.status] ?? item?.status;
            const data: ExportItem = {
              "S.NO": item?.sno,
              EPID: item?.id,
              "Registration No": item?.registrationNo,
              "Entity Name": item?.entityName,
              State: item?.state,
              District: item?.district,
              Division: item?.division,
              "Allocate Division": item?.allocatedivision,
              Range: item?.range,
              "Tree Canopy Density": item?.treeCanopy,
              "Total Area": item?.landArea,
              "Area Selected": item?.areaUnit,
              "Final Area": item?.netPlantationArea,
              "Created At": item?.created_at,
              "Updated At": item?.updated_at,
              "Total Amount Received": item?.totalAmountRecived,
              "Eco-Restoration Cost Received": item.ecoRestorationCostRecived,
              "Admin Charges Received": item.adminChargesRecived,
            };

            if (status === "UNDER_QUERY" || status === "VERIFICATION_PENDING") {
              data.Eco_Restoration_Cost = "-";
              data.DemandNote_Amount = "-";
            } else {
              data.Eco_Restoration_Cost = item?.demandNote ?? "-";
              data.DemandNote_Amount = item?.estimateAmount ?? "-";
            }

            data.Status = ExportStatusMessages2[item?.status] ?? item?.status;
            data.Remark = item?.remark;

            return data;
          });

          downloadSheet(exportData);
          setQueryParams((prev) => ({
            ...prev,
            download: false,
          }));
        } else {
          setProjectData({
            count: data?.data.count,
            rows,
          });
        }
      },
      refetchOnMount: true,
    },
  );

  const RenderEPID = (params) => {
    return (
      <>
        <Box
          component={RouterLink}
          to={`${path.landOwnershipDetails}?${createSearchParams({
            id: params?.id,
          })}`}
          sx={{
            ...defaultStyles.renderButtonText,
            color: "#36B1A0",
            fontWeight: 500,
            textDecoration: "none",
          }}
        >{`${params?.row?.id}`}</Box>
      </>
    );
  };

  const { entityInfo } = useLandOwnershipEntityOptions({
    mouStatus: queryParams.mouStatus,
    dprStatus: queryParams.dprStatus,
    newSelection: queryParams.newSelection,
    stateIds: queryParams.projectStateIds?.join(","),
    searchText: queryParams.searchText,
  });

  const tableColumns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 90,
      sortable: false,
      tab: [],
    },
    {
      field: "id",
      headerName: "ID",
      width: 200,
      sortable: false,
      renderCell: RenderEPID,
      tab: [],
    },
    {
      field: "entityName",
      headerName: "Entity Name",
      width: 200,
      sortable: false,
    },
    {
      field: "state",
      headerName: "State",
      width: 200,
      sortable: false,
    },
    {
      field: "district",
      headerName: "District",
      width: 200,
      sortable: false,
    },
    {
      field: "division",
      headerName: "Forest Division",
      width: 200,
      sortable: false,
    },
    {
      field: "range",
      headerName: "Range",
      width: 200,
      sortable: false,
      tab: [],
    },
    {
      field: "treeCanopy",
      headerName: "Tree Canopy Density",
      width: 200,
      sortable: false,
      tab: [],
    },
    {
      field: "landArea",
      headerName: "Total Area (Ha)",
      width: 200,
      sortable: false,
      tab: [],
    },
    {
      field: "created_at",
      headerName: "CREATED AT",
      width: 250,
      sortable: false,
      tab: [],
    },
    {
      field: "updated_at",
      headerName: "UPDATED AT",
      width: 250,
      sortable: true,
      tab: [],
    },
    {
      field: "mouStatus",
      headerName: "MOU Status",
      width: 200,
      sortable: false,
      tab: [],
      renderCell: (params) => {
        const mouStatus = params.row?.mouStatus;
        if (!mouStatus) return <Box>—</Box>;
        return (
          <Chip
            label={MOU_STATUS_LABELS[mouStatus] ?? mouStatus}
            size="small"
            sx={{
              backgroundColor: `${MOU_STATUS_COLORS[mouStatus] ?? "#757575"}20`,
              color: MOU_STATUS_COLORS[mouStatus] ?? "#757575",
              fontWeight: 600,
              fontSize: "12px",
            }}
          />
        );
      },
    },
    {
      field: "mou",
      headerName: "View/Upload MOU",
      width: 200,
      sortable: false,
      tab: [],
      renderCell: RenderMOUButton,
    },
  ];

  const handleSort = (sortByField) => {
    setQueryParams({
      ...queryParams,
      sortBy: `${sortByField[0]?.field}:${sortByField[0]?.sort.toUpperCase()}`,
    });
  };

  const exportToExcel = () => {
    setQueryParams({
      ...queryParams,
      download: true,
    });
  };

  return (
    <DashboardWrapper
      title="Memorandum of Understanding"
      buttonIcon="/assets/svg/roundPlusIcon.svg"
      buttonText=""
      onButtonClick={() => {}}
      showBtn={!!projectData.rows.length}
    >
      <>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: "2%",
            padding: "10px 1%",
            background: "white",
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
          }}
        >
          <SearchBar
            placeHolder="Search by Registration Number"
            defaultValue={persisted?.queryParams?.searchText || ""}
            getText={(e) => {
              setQueryParams((a) => {
                return { ...queryParams, searchText: `${e}` };
              });
            }}
          />
          <MultiSelect
            options={states}
            value={filterKey.projectStateIds}
            onChange={(e: any) => {
              setFilterKey((prevFilter) => {
                const newState = {
                  ...prevFilter,
                  projectStateIds: e.length ? e : null,
                };

                setQueryParams({
                  ...queryParams,
                  projectStateIds: newState?.projectStateIds,
                });

                return newState;
              });
            }}
          />
          <MultiSelect
            options={entityInfo}
            label={"Filter By Entity"}
            clearable
            value={entityfilterKey.entityIds}
            onChange={(e: any) => {
              entitySetFilterKey((prevFilter) => {
                const newState = {
                  ...prevFilter,
                  entityIds: e.length ? e : null,
                };
                setQueryParams({
                  ...queryParams,
                  entityIds: newState?.entityIds,
                  offset: 0,
                });
                return newState;
              });
            }}
          />
          <Button onClick={exportToExcel} text={"Export"} />
        </Box>
        <HorizontalTabs
          tabHeaders={memorandumTab}
          tabIndex={memorandumTab.findIndex(
            (item) => item.value === entityActiveTab,
          )}
          onChange={(e: any) => {
            dispatch(refetchAllProjects(true));
            setEntityActiveTab(e);
            navigate(`${path.memorandum}${e ? `?currentTab=${e}` : ""}`);
            const { dprStatus, ...rest } = queryParams;
            setQueryParams({
              ...rest,
              mouStatus: e,
              offset: 0,
              ...(isAllTab(e) ? { dprStatus: "DPR_APPROVED" } : {}),
            });
          }}
          components={[<></>, <></>]}
        />
        {isEntityFetching ? (
          <HomeTableSkeleton showTabs={false} columns={8} />
        ) : projectData.rows.length > 0 ? (
          <DataGrid
            rows={projectData.rows}
            columns={tableColumns}
            rowCount={projectData.rows.length}
            rowsPerPageOptions={[10]}
            sortingMode="server"
            onSortModelChange={handleSort}
            paginationMode="client"
            pageSize={50}
            columnBuffer={20}
            loading={isEntityFetching}
            getRowId={(row) => row.id}
          />
        ) : (
          <EmptyView
            heading="No Entry Found"
            subheading=""
            onClick={() => {}}
          />
        )}
      </>
    </DashboardWrapper>
  );
};

export default Memorandum;
